import { json } from 'd3-fetch'

export class ImageStore {
  private readonly depotId: string
  private readonly blobIds: string[]
  private db?: IDBDatabase

  constructor (depotId: string, blobIds: string[]) {
    this.depotId = depotId
    this.blobIds = blobIds
  }

  public async connect (): Promise<void> {
    return await new Promise((resolve, reject) => {
      const request = indexedDB.open(this.depotId, 1)

      request.onupgradeneeded = (event) => {
        const db = request.result
        if (!db.objectStoreNames.contains('images')) {
          db.createObjectStore('images', { keyPath: 'id' })
        }
      }

      request.onerror = (event) => {
        console.error('Error opening IndexedDB:', request.error)
        reject(request.error)
      }

      request.onsuccess = (event) => {
        this.db = request.result
        resolve()
      }
    })
  }

  private processBlobs (): void {
    this.blobIds.forEach((blobId: string, index: number) => {
      if (index < 60) {
        this.processBlob(blobId)
      }
    })
  }

  private processBlob (blobId: string): void {
    const transaction = this.db?.transaction(['images'], 'readwrite')
    const store = transaction?.objectStore('images')

    const request = store?.get(blobId)

    request.onsuccess = (event) => {
      const result = request.result

      if (result?.blob !== undefined) {
        console.log('Blob already in store', result.blob)
        return
      }

      json(`/api/v3/blobs/${blobId}.json`)
        .then((response) => {
          this.getBlobFromUrl(blobId, response.url)
            .then((blob) => {
              console.log('Blob fetched from URL', blob)
            })
            .catch((error) => {
              console.error('Error fetching blob from URL:', error)
            })
        })
        .catch((error) => {
          console.error('Error fetching blob URL:', error)
        })
    }

    request.onerror = (event) => {
      console.error('Error getting blobId from store:', blobId, request.error)
    }
  }

  private async getBlobUrl (blobId: string): Promise<string> {
    return await new Promise((resolve, reject) => {
      json(`/api/v3/blobs/${blobId}.json`)
        .then((response) => {
          resolve(response.url)
        })
        .catch((error) => {
          reject(error)
        })
    })
  }

  private async getBlobFromUrl (blobId: string, blobUrl: string): Promise<void> {
    try {
      const response = await fetch(blobUrl)
      const blob = await response.blob()
      this.storeBlob(blobId, blob)
    } catch (error) {
      console.error('Error fetching blob:', error)
    }
  }

  private storeBlob (blobId: string, blob: Blob): void {
    const transaction = this.db?.transaction(['images'], 'readwrite')
    const store = transaction?.objectStore('images')

    const request = store?.put({ id: blobId, blob })
    request.onsuccess = (event) => {}
    request.onerror = (event) => {}
  }

  public async getBlob (blobId: string, retries: number = 3, delay: number = 500): Promise<Blob | null> {
    for (let i = 0; i < retries; i++) {
      const blob = await this.tryGetBlob(blobId)
      if (blob != null) {
        return blob
      }
      await new Promise(resolve => setTimeout(resolve, delay))
    }
    console.log('Failed to load blob after retries:', blobId)
    return null
  }

  public async tryGetBlob (blobId: string): Promise<Blob | null> {
    return await new Promise((resolve) => {
      if (this.db == null) {
        console.log('DB not yet loaded')
        resolve(null)
        return
      }

      if (blobId === undefined || blobId === null) {
        console.log('Blob ID is undefined or null')
        resolve(null)
        return
      }

      const transaction = this.db.transaction(['images'], 'readonly')
      const store = transaction.objectStore('images')

      const request = store.get(blobId)

      request.onsuccess = (event) => {
        const result = request.result
        if (result?.blob === undefined) {
          this.getBlobUrl(blobId)
            .then((blobUrl) => {
              this.getBlobFromUrl(blobId, blobUrl)
                .then((blob) => {
                  resolve(blob)
                })
                .catch((error) => {
                  console.log('Error getting blob from URL:', error)
                  resolve(null)
                })
            })
            .catch((error) => {
              console.log('Error getting blob URL:', error)
              resolve(null)
            })
        } else {
          resolve(result.blob)
        }
      }

      request.onerror = (event) => {
        console.error('Error getting blob from store:', blobId, request.error)
        resolve(null)
      }
    })
  }
}
